import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Megaphone } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { ReleaseBody } from "@/components/whats-new/release-body";
import { releaseKeys } from "@/lib/query";

interface Release {
  id: string;
  title: string;
  body: string;
  /** Commit-level notes; absent for releases predating this repository. */
  technicalBody: string | null;
  releasedAt: string | null;
}

function releaseDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Public "What's new" surface — a collapsible list of released entries, newest
 * first, written for everyone (not engineers). The most recent release opens by
 * default; the rest collapse so the page stays scannable as releases pile up.
 */
export function WhatsNewPage() {
  const {
    data: releases = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: releaseKeys.public,
    queryFn: async (): Promise<Release[]> => {
      const res = await fetch("/api/releases");
      if (!res.ok) throw new Error("Failed to load releases");
      const json = await res.json();
      return json.data;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load releases"} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="What's New" description="The latest changes to Enable." />
      {releases.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No releases yet"
          description="Updates will appear here as they ship."
        />
      ) : (
        <div className="space-y-3">
          {releases.map((r, i) => (
            // Native <details> gives us accessible, JS-free collapsing; the
            // newest release is open on load.
            <details
              key={r.id}
              open={i === 0}
              className="group rounded-lg border bg-card [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3">
                <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180" />
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-[15px] font-semibold text-foreground">{r.title}</h2>
                  {r.releasedAt && (
                    <p className="text-xs text-muted-foreground">{releaseDate(r.releasedAt)}</p>
                  )}
                </div>
              </summary>
              <div className="border-t px-4 py-3.5">
                <ReleaseBody source={r.body} technical={r.technicalBody} />
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
